import type React from 'react';
import {
  showNotification,
  NotificationData as MantineNotificationProps,
  hideNotification,
  cleanNotifications,
  cleanNotificationsQueue,
} from '@mantine/notifications';

interface NotificationProps extends Omit<MantineNotificationProps, 'message'> {
  message?: React.ReactNode;
}

export default function useNotificationSystem() {
  const createNotification = (
    type: 'info' | 'success' | 'warning' | 'error' = 'info',
    {
      message = 'Something went wrong when processing your request. Please try again or contact us for support.',
      ...rest
    }: NotificationProps = {},
  ) => {
    const options: MantineNotificationProps = {
      autoClose: 5000,
      withCloseButton: type !== 'error',
      message,
      ...rest,
    };

    switch (type) {
      case 'success':
        options.color = 'green';
        break;
      case 'warning':
        options.color = 'orange';
        break;
      case 'error':
        options.color = 'red';
        break;
      default:
        options.color = 'blue';
        break;
    }

    return showNotification(options);
  };

  const destroyNotification = (id: string) => {
    return hideNotification(id);
  };

  const clearNotifications = (type: 'all' | 'queue' = 'all') => {
    if (type && type === 'queue') {
      return cleanNotificationsQueue();
    }
    return cleanNotifications();
  };

  return { createNotification, destroyNotification, clearNotifications };
}
